import React, { useState, useEffect } from 'react';

const EvaluationDisplay = ({ evaluation, rating }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  if (!evaluation) return null;

  // Parse the evaluation text into structured sections
  const parseEvaluation = (text) => {
    const sections = [];
    const lines = text.split('\n').filter(line => line.trim() !== '');
    
    // Check if this is structured text (has ** headers) or natural text
    const hasStructuredHeaders = lines.some(line => line.includes('**') && line.trim().startsWith('**'));
    
    if (!hasStructuredHeaders) {
      // Handle natural, conversational text
      // Look for common patterns like "Strengths:", "Weaknesses:", etc.
      let currentSection = null;
      let currentContent = [];
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        
        // Look for section patterns (word/phrase followed by colon at start of line)
        if (trimmedLine.match(/^(Summary|Strengths|Weaknesses|Actionable Insights?|Consensus Rating|Short Summary):/i)) {
          // Save previous section
          if (currentSection) {
            sections.push({
              title: currentSection,
              content: currentContent.join('\n')
            });
          }
          
          // Start new section
          const colonIndex = trimmedLine.indexOf(':');
          currentSection = trimmedLine.substring(0, colonIndex).trim();
          const restOfLine = trimmedLine.substring(colonIndex + 1).trim();
          currentContent = restOfLine ? [restOfLine] : [];
        } else {
          // Add to current section or create a general section
          if (!currentSection) {
            currentSection = "Evaluation";
          }
          currentContent.push(line);
        }
      }
      
      // Add the last section
      if (currentSection) {
        sections.push({
          title: currentSection,
          content: currentContent.join('\n')
        });
      }
      
      return sections;
    }
    
    // Original structured parsing for backward compatibility
    let currentSection = null;
    let currentContent = [];

    for (const line of lines) {
      // Check if this is a section header (starts with ** and ends with **)
      if (line.includes('**') && line.trim().startsWith('**')) {
        // Save previous section
        if (currentSection) {
          sections.push({
            title: currentSection,
            content: currentContent.join('\n')
          });
        }
        
        // Start new section
        currentSection = line.replace(/\*\*/g, '').trim().replace(/:/g, '');
        currentContent = [];
      } 
      // Check if this is a subsection (starts with single word followed by colon)
      else if (line.includes(':') && !line.startsWith('-') && !line.startsWith('•')) {
        currentContent.push(line);
      }
      // Regular content line
      else {
        currentContent.push(line);
      }
    }

    // Add the last section
    if (currentSection) {
      sections.push({
        title: currentSection,
        content: currentContent.join('\n')
      });
    }

    return sections;
  };

  // Format content within a section
  const formatContent = (content) => {
    return content.split('\n').map((line, index) => {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) return null;
      
      // Skip the "Additional Academic Credentials" section if it's empty or just placeholder
      if (trimmedLine.includes('[Note any unique degrees') || 
          trimmedLine.includes('Advanced degrees:') ||
          trimmedLine.includes('Specialized certifications:') ||
          trimmedLine.includes('Non-traditional academic path:')) {
        return null;
      }
      
      // Handle bullet points
      if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•')) {
        const bulletText = trimmedLine.substring(1).trim();
        // Extract label and content if in "Label: Content" format
        const colonIndex = bulletText.indexOf(':');
        if (colonIndex > 0 && colonIndex < 30) {
          const label = bulletText.substring(0, colonIndex).trim();
          const text = bulletText.substring(colonIndex + 1).trim();
          return (
            <div key={index} className={`mb-2 ${isMobile ? 'text-sm' : 'text-base'}`}>
              <span className="font-semibold text-gray-800">{label}:</span>
              <span className="ml-2 text-gray-700">{text}</span>
            </div>
          );
        } else {
          return (
            <div key={index} className={`mb-2 ${isMobile ? 'text-sm' : 'text-base'} text-gray-700 pl-4`}>
              • {bulletText}
            </div>
          );
        }
      }
      
      // Handle ratings
      if (trimmedLine.includes('Consensus Rating:') || trimmedLine.includes('Final Committee Rating:')) {
        return (
          <div key={index} className={`mt-4 p-3 bg-blue-50 rounded-lg ${isMobile ? 'text-sm' : 'text-base'}`}>
            <span className="font-semibold text-blue-800">{trimmedLine}</span>
          </div>
        );
      }
      
      // Handle "Next Step" lines
      if (trimmedLine.includes('Next Step:')) {
        return (
          <div key={index} className={`mt-3 p-2 bg-green-50 rounded ${isMobile ? 'text-sm' : 'text-base'}`}>
            <span className="text-green-800 font-medium">{trimmedLine}</span>
          </div>
        );
      }
      
      // Handle probability percentages
      if (trimmedLine.includes('PROBABILITY:') || trimmedLine.includes('%')) {
        return (
          <div key={index} className={`mb-2 ${isMobile ? 'text-sm' : 'text-base'}`}>
            <span className="font-semibold text-indigo-700">{trimmedLine}</span>
          </div>
        );
      }
      
      // Regular paragraph text
      return (
        <p key={index} className={`mb-2 ${isMobile ? 'text-sm' : 'text-base'} text-gray-700 leading-relaxed`}>
          {trimmedLine}
        </p>
      );
    }).filter(Boolean);
  };

  const sections = parseEvaluation(evaluation);

  return (
    <div className={`space-y-6 ${isMobile ? 'p-4' : 'p-6'}`}>
      {sections.map((section, index) => {
        // Skip empty or placeholder sections
        if (!section.content.trim() || 
            section.content.includes('[Note any unique degrees') ||
            section.title.includes('ADDITIONAL ACADEMIC CREDENTIALS') && 
            section.content.includes('The candidate does not have any additional degrees')) {
          return null;
        }

        return (
          <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className={`bg-gray-50 px-4 py-3 border-b border-gray-200 ${isMobile ? 'px-3 py-2' : 'px-4 py-3'}`}>
              <h4 className={`font-semibold text-gray-800 ${isMobile ? 'text-sm' : 'text-base'}`}>
                {section.title}
              </h4>
            </div>
            <div className={`p-4 ${isMobile ? 'p-3' : 'p-4'}`}>
              {formatContent(section.content)}
            </div>
          </div>
        );
      }).filter(Boolean)}
    </div>
  );
};

export default EvaluationDisplay; 